import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  SdfFlagLayout,
  SdfIcon,
  SdfInput,
  SdfButton,
} from '@waypoint/react-components';
import {
  isNotBlank,
  isEnterKeyPressed,
  commonPollingAPI,
} from '../../util/misc';
import { constants } from '../login/login-comp-constants';
import {
  PUSH_SUCCESSFUL,
  PUSH_FAILED,
  PUSH_DENIED,
  PUSH_TIMEDOUT,
  APPROVED,
  PENDING,
  DENIED,
  LOCKED,
} from '../../util/pendo/constants';

import { trackEvents } from '../../util/misc/common';
import {
  ACCESS_DENIED,
  PUSH_AUTH_DENIED,
} from '../../layouts/layout-constants';

const {
  SIGNIN_POLL_API_ENDPOINT,
  PUSH_POLLING_API_MAXATTEMPTS,
  PUSH_POLLING_API_INTERVAL_IN_MILLISECONDS,
} = constants;

function OTPPage(props) {
  const {
    currentlySending,
    pushStatusCheckSuccess,
    pushStatusCheckFail,
    pushStatusCheckRetry,
    pushVerificationAttempts,
    otp,
    setOTP,
    pushSession,
    authenticatorId,
    verifyOTP,
    authenticatorFormat,
    otpReissueRemainingAttempts,
    otpDeadEndScenario,
    isAppView,
    intl,
    signedInResponseType,
    otpVerifyResponseType,
    otpNumber,
    otpPageProps = {},
  } = props;

  const {
    iconCheck,
    title1,
    title2,
    label,
    qrCodeImage,
    qrIcon,
    qrTextTitle,
    qrTextDescription,
    primaryButton,
    secondaryButton,
  } = otpPageProps;

  const maxAttempts = window.env && window.env.PUSH_POLLING_API_MAXATTEMPTS
    ? window.env.PUSH_POLLING_API_MAXATTEMPTS
    : PUSH_POLLING_API_MAXATTEMPTS;
  const pollingInterval = window.env && window.env.PUSH_POLLING_API_INTERVAL_IN_MILLISECONDS
    ? window.env.PUSH_POLLING_API_INTERVAL_IN_MILLISECONDS
    : PUSH_POLLING_API_INTERVAL_IN_MILLISECONDS;

  const stopPollingRef = useRef(false); // Ref to track if polling should stop
  const isPollingRef = useRef(false); // Use useRef for isPolling

  const attemptsRef = useRef(0); // Use useRef for attempts
  const pushSessionRef = useRef(pushSession); // Use a ref to persist pushSession

  useEffect(() => {
    if (otpPageProps.type === 'PUSH_VERIFICATION_CHALLENGE') {
      pushSessionRef.current = pushSession;

      const startPolling = async () => {
        if (isPollingRef.current) return; // Prevent multiple polling calls or stop if requested
        isPollingRef.current = true;

        if (attemptsRef.current >= maxAttempts) {
          pushStatusCheckFail(ACCESS_DENIED);
          isPollingRef.current = false; // Reset the flag
          return;
        }

        // eslint-disable-next-line no-plusplus
        attemptsRef.current++;

        const body = {
          pushSession: pushSessionRef.current,
          authenticatorId,
        };

        try {
          const response = await commonPollingAPI(
            SIGNIN_POLL_API_ENDPOINT,
            body,
            'POST',
          );
          const data = await response.json();
          const result = JSON.stringify(data);

          if (data.pushStatus === APPROVED) {
            console.log('OTP verification succeeded:', response);
            trackEvents(PUSH_SUCCESSFUL);
            isPollingRef.current = false; // Reset the flag
            pushStatusCheckSuccess(data.pushSession);
            return; // Stop polling on success
          }
          if (data.pushStatus === PENDING) {
            pushStatusCheckRetry(data.pushSession);

            // Update the pushSessionRef with the latest value
            pushSessionRef.current = data.pushSession;
            // Wait for the polling interval before making the next call
            setTimeout(() => {
              if (!stopPollingRef.current) {
                // Check if polling should stop
                isPollingRef.current = false; // Reset the flag after the interval
                startPolling();
              }
            }, pollingInterval);
            return; // Ensure no further execution in this call
          }
          if (data.pushStatus === DENIED) {
            trackEvents(PUSH_DENIED);
            pushStatusCheckFail(PUSH_AUTH_DENIED);
          }
          if (data.pushStatus === LOCKED) {
            trackEvents(PUSH_FAILED);
            console.error('OTP verification failed:', result);
            pushStatusCheckFail(ACCESS_DENIED);
          }
          // Stop polling on failure
        } catch (error) {
          console.error('Error during polling:', error);
          pushStatusCheckFail(ACCESS_DENIED);
          // Stop polling on error
        }
      };

      // Delay the first polling call
      setTimeout(() => {
        startPolling();
      }, pollingInterval);

      return () => {
        // Cleanup logic if needed
        stopPollingRef.current = true; // Cleanup: stop polling when component unmounts
      };
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOtpVerifySuccess = signedInResponseType === 'SIGNED_IN'
    || otpVerifyResponseType === 'SIGNED_IN';

  const isOTPEntered = !currentlySending && isNotBlank(otp) && !isOtpVerifySuccess;

  const otpMask = {
    mask: '#######',
    definitions: {
      // <any single char>: <same type as mask (RegExp, Function, etc.)>
      // defaults are '0', 'a', '\*'
      '#': /[0-9]/,
    },
  };

  const handleKeyPressOnNexIn = (event) => {
    if (event.charCode === 13 && isOTPEntered) {
      event.preventDefault();
      !currentlySending && verifyOTP();
    }
  };
  const otpAttemptsExceeded = otpReissueRemainingAttempts === 0;

  return (
    <div>
      <form onSubmit={ primaryButton.clickFunction }>
        <div id="frmLogin">
          <div>
            <div>
              <div className="md:font-medium md:not-italic text-md md:leading-md mt-3">
                <SdfFlagLayout>
                  { iconCheck && (
                    <SdfIcon
                      slot="graphic"
                      shape="square"
                      icon="brand-adp"
                      class="text-4xl text-brand-secondary shadow-2 rounded-md box-border"
                    />
                  ) }
                  { ' ' }
                  <FormattedMessage
                    id={ title1 }
                    defaultMessage="We sent a code to"
                    description="otp page title"
                  />
                  { authenticatorFormat }
                  { '. ' }
                  <br />
                  { title2 && (
                    <FormattedMessage
                      id={ title2 }
                      defaultMessage="Enter it within 10 minutes"
                      description="otp page title"
                    />
                  ) }
                </SdfFlagLayout>
              </div>
              <div
                id="otpform"
                className="flex-row flex-1 text-center pt-4"
              >
                { otpNumber ? (
                  <div className="text-center w-full mt-6">
                    <div className="inline-block px-4 py-4 rounded bg-action-primary-100">
                      <div className="text-md font-semibold text-gray-600 mb-2">
                        <FormattedMessage
                          id={ label }
                          defaultMessage="Code"
                        />
                      </div>
                      <div className="text-4xl font-bold text-gray-800">
                        { otpNumber }
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <SdfInput
                      autoFocus
                      id="otp-page_passcode"
                      data-testid="otp-page_passcode"
                      type="text"
                      input-width="xs"
                      inputType="decimal"
                      autocomplete="one-time-code"
                      advancedMask={ otpMask }
                      label={ intl.formatMessage({
                        id: label,
                        defaultMessage: 'Passcode',
                        description: 'Passcode label',
                      }) }
                      maxlength={ 7 }
                      value={ otp }
                      onSdfInput={ (eve) => setOTP(eve.detail) }
                      onKeyPress={ handleKeyPressOnNexIn }
                    />
                    <br />
                    <br />
                  </>
                ) }
              </div>
              { qrCodeImage && (
                <div className="flex justify-between items-center bg-gray-100 px-3 py-1.5 mt-3 rounded-md shadow-md">
                  <div className="flex items-start">
                    <SdfIcon
                      icon={ qrIcon }
                      shape="square"
                      className="text-4xl text-inverse-secondary bg-action-destructive-600 shadow-2 rounded-lg box-border mr-4"
                    />
                    <div>
                      <div className="text-base font-bold mb-1">
                        <FormattedMessage
                          id={ qrTextTitle }
                          defaultMessage="Download the App"
                        />
                      </div>
                      <div className="text-xs text-gray-600 max-w-xs">
                        <FormattedMessage
                          id={ qrTextDescription }
                          defaultMessage="Scan the QR code with your mobile device to download the app."
                        />
                      </div>
                    </div>
                  </div>
                  <img
                    src={ qrCodeImage }
                    alt="QR Code for ADP Mobile App"
                    className="h-24 w-24"
                  />
                </div>
              ) }
              <div
                className={
                  isAppView
                    ? 'flex-row flex-1 mt-10 text-center'
                    : 'flex md:flex-row-reverse mt-20 md:mt-8 flex-col items-center'
                }
              >
                { (!qrCodeImage
                  || (primaryButton && pushVerificationAttempts > 0)) && (
                  <SdfButton
                    buttonStyle="link"
                    emphasis={ primaryButton.emphasis || 'primary' }
                    data-testid={ primaryButton.id }
                    id={ primaryButton.id }
                    className={
                      isAppView ? 'w-full p-2' : primaryButton.className
                    }
                    disabled={
                      !qrCodeImage ? !isOTPEntered || undefined : undefined
                    }
                    size="lg"
                    onClick={ primaryButton.clickFunction }
                    onKeyPress={ handleKeyPressOnNexIn }
                  >
                    <span>
                      <FormattedMessage
                        id={ primaryButton.labelId }
                        defaultMessage={ primaryButton.defaultMessage }
                        description={ primaryButton.description }
                      />
                    </span>
                  </SdfButton>
                ) }
                { secondaryButton && (
                  <SdfButton
                    id={ secondaryButton.id }
                    data-testid={ secondaryButton.id }
                    className={
                      isAppView ? 'w-full p-2' : secondaryButton.className
                    }
                    buttonStyle="link"
                    size="lg"
                    hidden={ otpAttemptsExceeded }
                    onClick={ secondaryButton.clickFunction }
                    onKeyPress={ (event) => {
                      isEnterKeyPressed(event.key)
                        && secondaryButton.clickFunction();
                    } }
                  >
                    { ' ' }
                    { otpDeadEndScenario && (
                      <span id="cancel_span_link">
                        <FormattedMessage
                          id={ secondaryButton.labelId }
                          defaultMessage={ secondaryButton.defaultMessage }
                          description={ secondaryButton.description }
                        />
                      </span>
                    ) }
                    { !otpDeadEndScenario && (
                      <FormattedMessage
                        id={ secondaryButton.deadEndMessage }
                        defaultMessage={ secondaryButton.defaultMessage }
                        description={ secondaryButton.description }
                      />
                    ) }
                  </SdfButton>
                ) }
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

OTPPage.propTypes = {
  authenticatorFormat: PropTypes.string,
  currentlySending: PropTypes.bool,
  otpDeadEndScenario: PropTypes.bool,
  setOTP: PropTypes.func.isRequired,
  pushStatusCheckSuccess: PropTypes.func,
  pushStatusCheckFail: PropTypes.func,
  pushStatusCheckRetry: PropTypes.func,
  pushVerificationAttempts: PropTypes.number,
  verifyOTP: PropTypes.func.isRequired,
  otp: PropTypes.string,
  pushSession: PropTypes.string,
  authenticatorId: PropTypes.string,
  signedInResponseType: PropTypes.string,
  otpVerifyResponseType: PropTypes.string,
  otpReissueRemainingAttempts: PropTypes.number,
  otpNumber: PropTypes.string,
  otpPageProps: PropTypes.shape({
    iconCheck: PropTypes.bool,
    type: PropTypes.string,
    title1: PropTypes.string,
    title2: PropTypes.string,
    label: PropTypes.string,
    qrIcon: PropTypes.string,
    qrTextTitle: PropTypes.string,
    qrTextDescription: PropTypes.string,
    qrCodeImage: PropTypes.string,
    primaryButton: PropTypes.shape({
      id: PropTypes.string,
      description: PropTypes.string,
      labelId: PropTypes.string,
      defaultMessage: PropTypes.string,
      className: PropTypes.string,
      clickFunction: PropTypes.func,
    }),
    secondaryButton: PropTypes.shape({
      id: PropTypes.string,
      description: PropTypes.string,
      labelId: PropTypes.string,
      defaultMessage: PropTypes.string,
      className: PropTypes.string,
      clickFunction: PropTypes.func,
      deadEndMessage: PropTypes.string,
    }),
  }),

  result: PropTypes.shape({
    challenge: PropTypes.shape({
      type: PropTypes.string.isRequired,
    }),
    stepupSession: PropTypes.string.isRequired,
  }),
  isAppView: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
};

export default injectIntl(OTPPage);
